class QuickAnnouncementBar {
    /**
     * 创建一个通知轮播组件实例
     * @param {Object} config - 配置选项
     */
    constructor(config) {
      // 默认配置
      this.defaultConfig = {
        change_slides_speed: 5, // 轮播间隔，单位秒
        animationType: "slide", // 动画类型：'fade'|'slide'|'zoom'
        slideDirection: "vertical", // 滑动方向：'horizontal'|'vertical'
        animationDuration: 500, // 动画过渡时间，单位毫秒
        height: {
          desktop: "50px", // 桌面端高度
          mobile: "40px", // 移动端高度
        },
        mobileBreakpoint: 768, // 移动设备的宽度断点，单位px
        max_text_length: 100, // 文本最大长度
        containerSelector: "#zz-quick-announcement-bar-container", // 容器选择器
        items: [], // 轮播项目数组
      };
  
      // 合并配置
      this.config = { ...this.defaultConfig, ...config };
  
      // 组件状态
      this.state = {
        currentIndex: 0,
        isPlaying: true,
        isAnimating: false, // 添加动画状态锁
        intervalId: null,
        container: null,
        items: [],
        itemElements: [],
        backgroundElements: [], // 背景元素数组
        contentElements: [], // 内容元素数组
        spacer: null,
      };

      // 翻转动画相关状态
      this.flipState = {
        previousTime: { days: null, hours: null, minutes: null, seconds: null },
        animationInProgress: new Set(),
        digitElements: new Map() // 存储数字元素的引用
      };
  
      // 初始化组件
      this.initialize();
    }
  
    /**
     * 初始化组件
     * @private
     */
    initialize() {
      // 创建容器元素
      this.createContainer();
  
      // 验证和处理配置项
      this.validateAndProcessConfig();
  
      // 创建轮播项目
      this.createItems();
  
      // 只有当有多个项目时才开始轮播
      if (this.config.items.length > 1) {
        this.play();
      } else {
        // 如果只有一个项目，不启动轮播
        this.pause(); 
      }
  
      // 绑定事件
      this.bindEvents();
    }
  
    /**
     * 验证和处理配置项
     * @private
     */
    validateAndProcessConfig() {
      // 处理高度配置兼容性
      if (typeof this.config.height === "string") {
        // 将字符串格式转换为对象格式
        this.config.height = {
          desktop: this.config.height,
          mobile: this.config.height, // 默认移动端使用同样高度
        };
      }
  
      // 验证轮播项目
      if (!Array.isArray(this.config.items) || this.config.items.length === 0) {
        console.warn("AnnouncementBar: No items provided in config");
        this.config.items = [];
      }
  
      // 处理文本长度和视频源
      this.config.items = this.config.items.map((item) => {
        // 如果是富文本，默认设置use_html为true
        if (item.text && typeof item.text === 'string' && (item.text.includes('<') && item.text.includes('>'))) {
          item.use_html = true;
        }
        
        // 处理文本长度
        if (item.text && item.text.length > this.config.max_text_length && !item.use_html) {
          item.text = item.text.substring(0, this.config.max_text_length) + "...";
        }
  
        // 处理视频背景源
        if (item.background && item.background.type === "video") {
          // 确保有默认视频源
          if (!item.background.src) {
            console.warn("AnnouncementBar: Video source is missing");
            // 回退到纯色背景
            item.background = { type: "color", color: "#4a90e2" };
          }
  
          // 如果没有指定移动视频源但有主视频源，可以设置同样的源
          if (!item.background.mobileVideoSrc && item.background.src) {
            // 允许使用相同的视频源，但记录一个警告
            console.info(
              "AnnouncementBar: Using same video source for mobile devices. Consider providing optimized version."
            );
          }
        }
  
        // 处理banner链接
        if (item.banner_link) {
          // 如果设置了banner链接，但没有设置目标，默认为当前窗口打开
          if (!item.banner_link_target) {
            item.banner_link_target = "_self";
          }
  
          // 如果同时设置了bannerLink和link，记录警告
          if (item.link) {
            console.warn(
              "AnnouncementBar: Both bannerLink and link are set. Text link may override banner link when clicked."
            );
          }
        }
  
        return item;
      });
    }
  
    /**
     * 创建容器元素
     * @private
     */
    createContainer() {
      // 查找现有容器或创建新容器
      let container = document.querySelector(this.config.containerSelector);
  
      if (!container) {
        container = document.createElement("div");
        container.id = this.config.containerSelector.replace("#", "");
        document.body.insertBefore(container, document.body.firstChild);
      }
  
      // 添加容器类名
      container.classList.add("zz_qab-announcement-bar-container");
  
      // 设置容器样式
      container.style.width = "100%";
      this.applyResponsiveHeight(container);
      container.style.overflow = "hidden";
  
      // 动态创建和管理 spacer 元素
      this.createOrUpdateSpacer();
  
      // 存储容器引用
      this.state.container = container;
    }
  
    /**
     * 创建或更新 spacer 元素，为 announcement bar 预留空间
     * @private
     */
    createOrUpdateSpacer() {
      // 查找现有的 spacer 元素
      let spacer = document.getElementById('zz-banner-spacer');
      
      if (!spacer) {
        // 创建新的 spacer 元素
        spacer = document.createElement('div');
        spacer.id = 'zz-banner-spacer';
        spacer.innerHTML = '&nbsp;';
        document.body.insertBefore(spacer, document.body.firstChild);
      }
      
      // 应用响应式高度到 spacer
      this.applyResponsiveHeightToSpacer(spacer);
      
      // 显示 spacer（如果之前隐藏了）
      spacer.style.display = 'block';
      
      // 存储 spacer 引用
      this.state.spacer = spacer;
    }

    /**
     * 应用响应式高度到 spacer 元素
     * @param {HTMLElement} spacer - spacer 元素
     * @private
     */
    applyResponsiveHeightToSpacer(spacer) {
      // 获取 announcement bar 的配置高度
      const heightConfig = this.config.height;
      const mobileBreakpoint = this.config.mobileBreakpoint || 768;
      
      // 检测当前设备类型
      const isMobileView = window.matchMedia(`(max-width: ${mobileBreakpoint}px)`).matches;
      
      // 计算 spacer 的高度（与 announcement bar 保持一致）
      let height;
      if (typeof heightConfig === "object") {
        height = isMobileView
          ? heightConfig.mobile || heightConfig.desktop || "40px"
          : heightConfig.desktop || "50px";
      } else {
        height = heightConfig || "50px";
      }
      
      // 应用高度
      spacer.style.height = height;
    }
  
    /**
     * 创建轮播项目
     * @private
     */
    createItems() {
      // 清空容器
      this.state.container.innerHTML = "";
      this.state.itemElements = [];
      this.state.backgroundElements = [];
      this.state.contentElements = [];
  
      // 为每个项目创建DOM元素
      this.config.items.forEach((item, index) => {
        // 创建主容器
        const itemElement = document.createElement("div");
        itemElement.className = "zz_qab-announcement-bar-item";
        itemElement.style.position = "absolute";
        itemElement.style.top = "0";
        itemElement.style.left = "0";
        itemElement.style.width = "100%";
        itemElement.style.height = "100%";
        itemElement.style.opacity = index === 0 ? "1" : "0";
        itemElement.style.zIndex = index === 0 ? "1" : "0";
  
        // 如果配置了bannerLink，则使整个banner可点击
        if (item.banner_link) {
          itemElement.style.cursor = "pointer";
          itemElement.addEventListener("click", (e) => {
            // 只有当点击的不是按钮或链接时才触发banner的点击事件
            if (!e.target.closest("button") && !e.target.closest("a")) {
              if (item.banner_link_target === "_blank") {
                window.open(item.banner_link, "_blank");
              } else {
                window.location.href = item.banner_link;
              }
            }
          });
        }
  
        // 创建背景层
        const backgroundLayer = document.createElement("div");
        backgroundLayer.className = "zz_qab-announcement-background-layer";
        backgroundLayer.style.position = "absolute";
        backgroundLayer.style.top = "0";
        backgroundLayer.style.left = "0";
        backgroundLayer.style.width = "100%";
        backgroundLayer.style.height = "100%";
        backgroundLayer.style.zIndex = "1";
        backgroundLayer.style.backgroundColor = item.background_color || "#ffffff";
        backgroundLayer.innerHTML = "&nbsp;"
        // 设置背景（无动画）
        // this.setItemBackground(backgroundLayer, item);
        
        // 创建新的布局容器层
        const layoutLayer = document.createElement("div");
        layoutLayer.className = "zz_qab-announcement-layout-layer";
        layoutLayer.style.position = "absolute";
        layoutLayer.style.top = "0";
        layoutLayer.style.left = "0";
        layoutLayer.style.right = "0";
        layoutLayer.style.bottom = "0";
        layoutLayer.style.display = "flex";
        layoutLayer.style.justifyContent = "center";
        layoutLayer.style.alignItems = "center";
        layoutLayer.style.zIndex = "2";
        
        // 创建左侧导航链接
        const homeLink = document.createElement("a");
        homeLink.href = this.config.home_link_url || "/";
        homeLink.className = "zz_qab-home-link";
        homeLink.textContent = this.config.home_link_text || "Home";
        homeLink.style.color = item.home_link_color || "#000000";
        homeLink.style.opacity = item.home_link_opacity || 0.7;

        // 创建内容层
        const contentLayer = document.createElement("div");
        contentLayer.className = "zz_qab-announcement-content-layer";
        contentLayer.classList.add(
          `zz_qab-announcement-animation-${this.config.animationType}`
        );
        if (this.config.animationType === "slide") {
          contentLayer.classList.add(
            `zz_qab-announcement-slide-${this.config.slideDirection}`
          );
        }
        contentLayer.style.position = "relative"; // 从 absolute 改为 relative
        contentLayer.style.height = "100%";
        contentLayer.style.display = "flex";
        contentLayer.style.alignItems = "center";
        contentLayer.style.justifyContent = "center";
        contentLayer.style.textAlign = item.text_align || "center";
        contentLayer.style.color = item.text_color || "#000000";
        contentLayer.style.transition = `opacity ${this.config.animation_duration}ms ease, transform ${this.config.animation_duration}ms ease`;
  
        // 设置初始transform值
        const isHorizontal = this.config.slideDirection === "horizontal";
        if (index === 0) {
          // 第一个项目始终在中心位置
          contentLayer.style.transform = "translateX(0)";
          if (!isHorizontal) {
            contentLayer.style.transform = "translateY(0)";
          }
        } else {
          // 非第一个项目需要设置在屏幕外，以便正确进入
          const slideValue = 100; // 默认向右/向下偏移100%
          if (isHorizontal) {
            contentLayer.style.transform = `translateX(${slideValue}%)`;
          } else {
            contentLayer.style.transform = `translateY(${slideValue}%)`;
          }
        }
  
        // 创建内容
        this.createItemContent(contentLayer, item);
  
        // 将各层添加到DOM中
        layoutLayer.appendChild(homeLink);
        layoutLayer.appendChild(contentLayer);
        
        itemElement.appendChild(backgroundLayer);
        itemElement.appendChild(layoutLayer);
  
        // 添加到容器和状态
        this.state.container.appendChild(itemElement);
        this.state.itemElements.push(itemElement);
        this.state.backgroundElements.push(backgroundLayer);
        this.state.contentElements.push(contentLayer);
      });

      // 处理所有收集的 link_map 元素
      // 使用 setTimeout 确保 DOM 元素已经完全添加到页面
      setTimeout(() => {
        this.processLinkMapElements();
      }, 0);
    }
  
    /**
     * 设置项目背景
     * @param {HTMLElement} element - 项目元素
     * @param {Object} item - 项目配置
     * @private
     */
    setItemBackground(element, item) {
      const background = item.background;
  
      if (!background) {
        element.style.backgroundColor = "#ffffff";
        return;
      }
  
      switch (background.type) {
        case "image":
          element.style.backgroundImage = `url(${background.src})`;
          element.style.backgroundSize = background.fit || "cover";
          element.style.backgroundPosition = "center";
          element.style.backgroundRepeat = "no-repeat";
          break;
  
        case "video":
          // 检测屏幕宽度
          const isMobileView = window.matchMedia("(max-width: 768px)").matches;
  
          // 设置背景图作为视频加载前的占位符
          if (background.mobileFallback) {
            element.style.backgroundImage = `url(${background.mobileFallback})`;
            element.style.backgroundSize = background.fit || "cover";
            element.style.backgroundPosition = "center";
            element.style.backgroundRepeat = "no-repeat";
          }
  
          // 在所有设备上创建视频元素，不再区分移动设备
          const video = document.createElement("video");
          // 添加唯一类名，便于CSS选择器定位
          const videoClassName = `zz_qab-announcement-video-${
            this.state.currentIndex || 0
          }`;
          video.className = `zz_qab-announcement-video ${videoClassName}`;
          video.src = background.src;
          video.autoplay = true;
          video.loop = true;
          video.muted = true;
          video.playsInline = true;
          video.style.position = "absolute";
          video.style.top = "0";
          video.style.left = "0";
          video.style.width = "100%";
          video.style.height = "100%";
          video.style.objectFit = background.fit || "cover";
          video.style.zIndex = "-1";
  
          // 针对移动设备优化视频设置
          if (isMobileView) {
            // 移动设备上降低视频质量以提高性能
            video.setAttribute("playsinline", "");
            video.setAttribute("webkit-playsinline", "");
  
            // 降低移动设备上的视频质量
            if (background.mobileVideoSrc) {
              // 如果提供了移动设备专用的视频源，使用它
              video.src = background.mobileVideoSrc;
            }
          }
  
          // 预加载设置
          video.preload = "auto";
  
          // 错误处理 - 如果视频加载失败，回退到图片
          video.addEventListener("error", () => {
            video.style.display = "none";
            console.warn("Video failed to load, falling back to image");
          });
  
          element.appendChild(video);
  
          // 添加针对视频优化的窗口大小变化监听
          this.addVideoResizeListener(video, isMobileView);
          break;
  
        case "color":
        default:
          // 处理渐变背景或普通颜色
          const color = background.color || "#ffffff";
          if (color.includes("gradient")) {
            element.style.backgroundImage = color;
          } else {
            element.style.backgroundColor = color;
          }
          break;
      }
    }
  
    /**
     * 添加针对视频优化的窗口大小变化监听
     * @param {HTMLVideoElement} videoElement - 视频元素
     * @param {boolean} isMobileInitially - 初始化时是否为移动设备
     * @private
     */
    addVideoResizeListener(videoElement, isMobileInitially) {
      const resizeHandler = this.throttle(() => {
        const isMobileView = window.matchMedia("(max-width: 768px)").matches;
  
        // 如果设备类型改变（在移动和桌面之间切换）
        if (isMobileView !== isMobileInitially) {
          // 重新加载视频以适应新环境
          const currentTime = videoElement.currentTime;
          videoElement.load();
          videoElement.currentTime = currentTime;
  
          // 尝试播放视频
          videoElement.play().catch((err) => {
            console.warn("Auto-play failed:", err);
          });
        }
  
        // 调整视频播放性能
        if (isMobileView) {
          // 在移动设备上，可能需要调整视频参数以优化性能
          if (videoElement.playbackRate !== 1) {
            videoElement.playbackRate = 1; // 恢复正常播放速度
          }
        }
      }, 250);
  
      window.addEventListener("resize", resizeHandler);
  
      // 确保在组件销毁时移除监听器
      if (!this._resizeListeners) {
        this._resizeListeners = [];
  
        // 扩展destroy方法，清理resize监听器
        const originalDestroy = this.destroy;
        this.destroy = () => {
          originalDestroy.call(this);
  
          // 清理所有resize监听器
          this._resizeListeners.forEach((handler) => {
            window.removeEventListener("resize", handler);
          });
          this._resizeListeners = [];
        };
      }
  
      this._resizeListeners.push(resizeHandler);
    }
  
    /**
     * 创建项目内容
     * @param {HTMLElement} element - 项目元素
     * @param {Object} item - 项目配置
     * @private
     */
    createItemContent(element, item) {
      // 创建容器以便更好地控制内容布局
      const contentContainer = document.createElement("div");
      contentContainer.classList.add("zz_qab-announcement-content-container");
      contentContainer.style.width = "840px";
      contentContainer.style.maxWidth = "840px";
      contentContainer.style.boxSizing = "border-box";
      contentContainer.style.textAlign = item.text_align || "center";
      contentContainer.style.display = "flex";
      contentContainer.style.flexDirection = "row";
      contentContainer.style.justifyContent = "center";
      contentContainer.style.alignItems = "center";
      contentContainer.style.flexWrap = "nowrap";

      // 添加动态对齐CSS类
      // 检查是否有任何图片配置
      const hasMobileImage = item.mobile_inline_image_url && item.mobile_inline_image_url.trim() !== "";
      const hasDesktopImage = item.desktop_inline_image_url && item.desktop_inline_image_url.trim() !== "";

      // 创建新的包装器（替代原 textWrapper 的位置，包含文本和倒计时）
      const mainWrapper = document.createElement("div");
      mainWrapper.style.flex = "0 1 auto";
      mainWrapper.style.display = "flex";
      mainWrapper.style.flexDirection = "column";
      if (hasMobileImage) {
        mainWrapper.classList.add("zz_qab-has-mobile-image");
      }
      if (hasDesktopImage) {
        mainWrapper.classList.add("zz_qab-has-desktop-image");
      }
      mainWrapper.classList.add("zz_qab-announcement-main-wrapper");

      // 创建文本内容包装器（移入新包装器内）
      const textWrapper = document.createElement("div");
      textWrapper.style.display = "flex";
      textWrapper.style.alignItems = "center";
      textWrapper.style.justifyContent = "center"; // 默认居中，CSS会覆盖
      textWrapper.classList.add("zz_qab-announcement-text-wrapper");
      
      if (hasMobileImage || hasDesktopImage) {
        textWrapper.classList.add("zz_qab-text-with-image");
      } else {
        textWrapper.classList.add("zz_qab-text-no-image");
      }

      // 检查是否有移动端专用文本
      const hasMobileText = item.mobile_text && item.mobile_text.trim() !== "";


      // 创建文本元素的通用函数
      const createTextElement = (textContent, isMobile = false) => {
        // 检查是否真的有链接值，如果没有则创建div而不是a元素
        const hasValidLink = (
          (item.function_type === 'link' && item.url && item.url.trim() !== '') ||
          (item.function_type === 'link_map' && item.links && item.links.trim() !== '') ||
          (item.legacy_link && item.legacy_link.trim() !== '')
        );
        
        const element = document.createElement(hasValidLink ? "a" : "div");
        
        // 设置链接属性
        if (element.tagName === "A") {
          if (item.function_type === 'link' && item.url) {
            element.href = item.url;
          } else if (item.function_type === 'link_map') {
            element.href = "#"; // 占位符，后续会被link_map处理
          } else if (item.legacy_link) {
            element.href = item.legacy_link;
          }
          element.target = item.link_target || "_blank";
          element.style.color = "inherit";
          element.style.textDecoration = item.text_decoration || "none";
        }

        // 设置文本内容
        if (item.use_html) {
          element.innerHTML = this.sanitizeHtml(textContent);
          element.style.whiteSpace = "normal";
          element.style.overflow = "visible";
          element.style.textOverflow = "initial";
        } else {
          element.textContent = this.truncateText(textContent, this.config.max_text_length);
          element.style.display = "inline-block";
          element.style.overflow = "hidden";
          element.style.textOverflow = "ellipsis";
          element.style.whiteSpace = "nowrap";
        }

        // 添加响应式类
        if (hasMobileText) {
          element.classList.add(isMobile ? "zz_qab-mobile-only" : "zz_qab-desktop-only");
        }

        // 添加下划线悬停效果
        if (item.hover_underline && element.tagName === "A") {
          element.style.borderBottom = "1px solid transparent";
          element.style.transition = "border-color 0.3s ease";
          element.addEventListener("mouseenter", () => {
            element.style.borderBottomColor = "currentColor";
          });
          element.addEventListener("mouseleave", () => {
            element.style.borderBottomColor = "transparent";
          });
        }

        return element;
      };

      // 富文本内容样式调整
      if (item.use_html) {
        textWrapper.style.overflow = "visible"; // 允许内容溢出
        textWrapper.style.whiteSpace = "normal"; // 允许换行
        textWrapper.style.lineHeight = "1.4"; // 适当的行高
        textWrapper.style.maxHeight = "100%"; // 限制最大高度
      }

      // 应用文本样式
      if (item.font_weight) {
        textWrapper.style.fontWeight = item.font_weight;
      }

      if (item.text_shadow) {
        textWrapper.style.textShadow = item.text_shadow;
      }

      if (item.function_type === 'link' && item.url) {
        // 普通链接处理 - 创建桌面端文本
        const desktopElement = createTextElement(item.text, false);
        textWrapper.appendChild(desktopElement);
        
        // 如果有移动端文本，创建移动端文本
        if (hasMobileText) {
          const mobileElement = createTextElement(item.mobile_text, true);
          textWrapper.appendChild(mobileElement);
        }
      } else if (item.function_type === 'link_map' && item.links) {
        // 多国家映射处理 - 创建桌面端文本
        const desktopElement = createTextElement(item.text, false);
        textWrapper.appendChild(desktopElement);
        
        // 收集需要处理的link_map元素
        if (!this._linkMapElements) {
          this._linkMapElements = [];
        }
        this._linkMapElements.push({
          element: desktopElement,
          links: item.links
        });
        
        // 如果有移动端文本，创建移动端文本
        if (hasMobileText) {
          const mobileElement = createTextElement(item.mobile_text, true);
          textWrapper.appendChild(mobileElement);
          
          // 移动端元素也需要添加到link_map处理列表
          this._linkMapElements.push({
            element: mobileElement,
            links: item.links
          });
        }
      } else if (item.legacy_link) {
        // 向后兼容：处理旧的 link 字段 - 创建桌面端文本
        const desktopElement = createTextElement(item.text, false);
        textWrapper.appendChild(desktopElement);
        
        // 如果有移动端文本，创建移动端文本
        if (hasMobileText) {
          const mobileElement = createTextElement(item.mobile_text, true);
          textWrapper.appendChild(mobileElement);
        }
      } else {
        // 创建纯文本 - 创建桌面端文本
        const desktopElement = createTextElement(item.text, false);
        textWrapper.appendChild(desktopElement);
        
        // 如果有移动端文本，创建移动端文本
        if (hasMobileText) {
          const mobileElement = createTextElement(item.mobile_text, true);
          textWrapper.appendChild(mobileElement);
        }
      }

      // 创建倒计时展示区域
      const countdownArea = document.createElement("div");
      countdownArea.classList.add("zz_qab-announcement-countdown-area");
      countdownArea.style.display = "flex";
      countdownArea.style.alignItems = "center";
      countdownArea.style.justifyContent = "center"; // 默认居中，CSS会覆盖
      countdownArea.style.fontSize = "12px";
      
      // 添加动态对齐CSS类
      if (hasMobileImage || hasDesktopImage) {
        countdownArea.classList.add("zz_qab-countdown-with-image");
      } else {
        countdownArea.classList.add("zz_qab-countdown-no-image");
      }
      
      // 如果配置了倒计时，创建倒计时内容
      if (item.countdown) {
        this.createCountdownContent(countdownArea, item.countdown);
      }

      // 将文本包装器和倒计时区域添加到主包装器
      mainWrapper.appendChild(textWrapper);
      mainWrapper.appendChild(countdownArea);

      // 将主包装器添加到内容容器
      contentContainer.appendChild(mainWrapper);

      // 如果图片位置在右侧，添加图片
      if (item.inline_image_url && item.image_position === "right") {
        // 创建图片元素
        const imgElement = document.createElement("img");
        imgElement.src = item.inline_image_url;
        imgElement.alt =
          typeof item.text === "string"
            ? this.stripHtml(item.text)
            : "Announcement image";
        imgElement.className = "zz_qab-announcement-inline-image";
        imgElement.classList.add("zz_qab-announcement-image-right");
        imgElement.style.width = `${item.inline_image_width || item.image_width || 30}px`;
        imgElement.style.height = "auto";
        imgElement.style.flexShrink = "0";

        contentContainer.appendChild(imgElement);
      }

      // 添加辅助按钮（如果配置了）
      if (item.button) {
        const btnWrapper = document.createElement("div");
        btnWrapper.style.flex = "0 0 auto";
        btnWrapper.style.marginLeft = "15px";

        const btn = document.createElement("button");
        btn.className = "zz_qab-announcement-button";
        btn.textContent = item.button.text;
        btn.style.padding = "4px 12px";
        btn.style.background = item.button.background || "#ffffff";
        btn.style.color = item.button.color || "#333333";
        btn.style.border = "none";
        btn.style.borderRadius = "2px";
        btn.style.cursor = "pointer";
        btn.style.fontSize = "12px";
        btn.style.fontWeight = "bold";
        btn.style.lineHeight = "1.5";
        btn.style.whiteSpace = "nowrap";
        btn.style.transition = "background 0.3s ease";

        // 添加按钮事件
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (item.button.link) {
            window.open(item.button.link, item.button.target || "_blank");
          }
          return false; // 确保不会触发容器的点击事件
        });

        btnWrapper.appendChild(btn);
        contentContainer.appendChild(btnWrapper);
      }

      // 处理内联图片（同时支持移动端和桌面端）
      if (item.mobile_inline_image_url || item.desktop_inline_image_url) {
        // 创建移动端图片元素
        if (item.mobile_inline_image_url) {
          const mobileImgElement = document.createElement("img");
          mobileImgElement.src = item.mobile_inline_image_url;
          mobileImgElement.alt =
            typeof item.text === "string"
              ? this.stripHtml(item.text)
              : "Announcement image";
          mobileImgElement.className =
            "zz_qab-announcement-inline-image zz_qab-mobile-only";

          // 添加位置相关的类
          if (item.image_position === "left" || !item.image_position) {
            mobileImgElement.classList.add("zz_qab-announcement-image-left");
          } else {
            mobileImgElement.classList.add("zz_qab-announcement-image-right");
          }

          mobileImgElement.style.width = `${item.mobile_inline_image_width || 132}px`;
          mobileImgElement.style.height = "auto";
          mobileImgElement.style.flexShrink = "0";

          // 根据图片位置决定添加顺序
          if (item.image_position === "left" || !item.image_position) {
            // 左侧位置：添加到开头
            if (contentContainer.firstChild) {
              contentContainer.insertBefore(
                mobileImgElement,
                contentContainer.firstChild
              );
            } else {
              contentContainer.appendChild(mobileImgElement);
            }
          } else {
            // 右侧位置：添加到末尾
            contentContainer.appendChild(mobileImgElement);
          }
        }

        // 创建桌面端图片元素
        if (item.desktop_inline_image_url) {
          const desktopImgElement = document.createElement("img");
          desktopImgElement.src = item.desktop_inline_image_url;
          desktopImgElement.alt =
            typeof item.text === "string"
              ? this.stripHtml(item.text)
              : "Announcement image";
          desktopImgElement.className =
            "zz_qab-announcement-inline-image zz_qab-desktop-only";

          // 添加位置相关的类
          if (item.image_position === "left" || !item.image_position) {
            desktopImgElement.classList.add("zz_qab-announcement-image-left");
          } else {
            desktopImgElement.classList.add("zz_qab-announcement-image-right");
          }

          desktopImgElement.style.width = `${item.desktop_inline_image_width || 300}px`;
          desktopImgElement.style.height = "auto";
          desktopImgElement.style.flexShrink = "0";
          console.log("desktopImgElement", item.image_position);

          // 根据图片位置决定添加顺序
          if (item.image_position === "left" || !item.image_position) {
            // 左侧位置：添加到开头
            if (contentContainer.firstChild) {
              contentContainer.insertBefore(
                desktopImgElement,
                contentContainer.firstChild
              );
            } else {
              contentContainer.appendChild(desktopImgElement);
            }
          } else {
            // 右侧位置：添加到末尾
            contentContainer.appendChild(desktopImgElement);
          }
        }

        contentContainer.style.justifyContent = "space-between";
      }

      element.appendChild(contentContainer);
    }

    /**
     * 处理多国家映射链接
     * @private
     */
    processLinkMapElements() {
      if (!this._linkMapElements || this._linkMapElements.length === 0) {
        return;
      }

      // 确保 bindSiteJump 函数已加载
      if (typeof bindSiteJump === 'function') {
        this._linkMapElements.forEach(({ element, links }) => {
          try {
            bindSiteJump(element, links);
          } catch (error) {
            console.warn('Error processing link_map element:', error);
            // 降级处理：如果 bindSiteJump 失败，尝试解析默认链接
            const defaultLink = this.parseDefaultLink(links);
            if (defaultLink) {
              element.href = defaultLink;
            }
          }
        });
      } else {
        console.warn('bindSiteJump function not found, processing link_map elements with fallback');
        // 降级处理：解析默认链接
        this._linkMapElements.forEach(({ element, links }) => {
          const defaultLink = this.parseDefaultLink(links);
          if (defaultLink) {
            element.href = defaultLink;
          }
        });
      }

      // 清空已处理的元素
      this._linkMapElements = [];
    }

    /**
     * 解析默认链接（降级处理）
     * @param {string} links - 链接映射字符串
     * @returns {string|null} 默认链接或null
     * @private
     */
    parseDefaultLink(links) {
      if (!links || typeof links !== 'string') {
        return null;
      }

      try {
        // 按行分割
        const lines = links.split('\n');
        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.toLowerCase().startsWith('default:')) {
            // 提取默认链接
            const defaultLink = trimmedLine.substring(8).trim(); // 移除 'default:' 前缀
            return defaultLink;
          }
        }
      } catch (error) {
        console.warn('Error parsing default link:', error);
      }

      return null;
    }

    /**
     * 基于主色动态生成渐变
     * @param {string} baseColor - 基础颜色（如 "#FF9B26"）
     * @returns {string} CSS渐变字符串
     * @private
     */
    generateCountdownGradient(baseColor) {
      try {
        // 将十六进制颜色转换为RGB
        const rgb = this.hexToRgb(baseColor);
        if (!rgb) {
          return baseColor; // 如果转换失败，返回原色
        }

        // 生成浅化版本（增加白色混合）
        const lightRgb = {
          r: Math.min(255, rgb.r + (255 - rgb.r) * 0.4),
          g: Math.min(255, rgb.g + (255 - rgb.g) * 0.4),
          b: Math.min(255, rgb.b + (255 - rgb.b) * 0.4)
        };

        const lightColor = `rgb(${Math.round(lightRgb.r)}, ${Math.round(lightRgb.g)}, ${Math.round(lightRgb.b)})`;
        
        // 生成类似于您提供的渐变样式
        return `linear-gradient(135.76deg, ${baseColor} 4.83%, ${lightColor} 94.13%)`;
      } catch (error) {
        console.warn('Error generating gradient:', error);
        return baseColor;
      }
    }

    /**
     * 将十六进制颜色转换为RGB
     * @param {string} hex - 十六进制颜色（如 "#FF9B26"）
     * @returns {Object|null} RGB对象 {r, g, b} 或 null
     * @private
     */
    hexToRgb(hex) {
      // 移除 # 号
      hex = hex.replace('#', '');
      
      // 支持3位和6位十六进制
      if (hex.length === 3) {
        hex = hex.split('').map(char => char + char).join('');
      }
      
      if (hex.length !== 6) {
        return null;
      }

      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      return { r, g, b };
    }

    /**
     * 创建翻转数字盒子
     * @param {string} initialValue - 初始数字值
     * @param {string} elementId - 元素唯一ID
     * @param {Object} countdownConfig - 倒计时配置
     * @returns {Object} 包含容器和包装器的对象
     * @private
     */
    createFlipTimeBox(initialValue, elementId, countdownConfig) {
      const container = document.createElement("span");
      container.className = "digit-flip-container";
      
      // 应用背景样式
      const backgroundColor = countdownConfig.background_color || "#FF9B26";
      const useGradient = countdownConfig.use_gradient || false;
      const boxBackground = useGradient 
        ? this.generateCountdownGradient(backgroundColor)
        : backgroundColor;
      
      if (useGradient) {
        container.style.background = boxBackground;
      } else {
        container.style.backgroundColor = boxBackground;
      }
      
      // 其他样式由CSS处理，确保与普通数字盒子保持一致
      
      const wrapper = document.createElement("div");
      wrapper.className = "digit-flip-wrapper";
      wrapper.setAttribute("data-current", initialValue);
      wrapper.dataset.id = elementId;
      wrapper.style.transitionDuration = `${countdownConfig.flip_duration}ms`;
      
      // 创建0-9所有数字
      for (let i = 0; i <= 9; i++) {
        const digit = document.createElement("span");
        digit.setAttribute("data-digit", i);
        digit.textContent = i;
        wrapper.appendChild(digit);
      }
      
      container.appendChild(wrapper);
      
      // 存储元素引用
      this.flipState.digitElements.set(elementId, wrapper);
      
      return { container, wrapper };
    }

    /**
     * 更新翻转数字
     * @param {string} elementId - 元素ID
     * @param {string} newValue - 新数字值
     * @private
     */
    updateFlipDigit(elementId, newValue) {
      const wrapper = this.flipState.digitElements.get(elementId);
      if (!wrapper) return;
      
      const currentValue = wrapper.getAttribute("data-current");
      if (currentValue === newValue) return; // 数字未变化
      
      // 检查是否正在动画中
      if (this.flipState.animationInProgress.has(elementId)) {
        return; // 跳过这次更新，避免动画冲突
      }
      
      // 标记动画开始
      this.flipState.animationInProgress.add(elementId);
      
      // 更新数字
      wrapper.setAttribute("data-current", newValue);
      
      // 动画完成后清理状态
      const duration = parseInt(wrapper.style.transitionDuration) || 600;
      setTimeout(() => {
        this.flipState.animationInProgress.delete(elementId);
      }, duration);
    }
  
    /**
     * 创建倒计时内容
     * @param {HTMLElement} container - 倒计时容器元素
     * @param {Object} countdownConfig - 倒计时配置
     * @private
     */
    createCountdownContent(container, countdownConfig) {
      if (!countdownConfig || !countdownConfig.target_date) {
        return;
      }

      // 解析目标日期
      const targetDate = new Date(countdownConfig.target_date);
      if (isNaN(targetDate.getTime())) {
        console.warn("Invalid target date for countdown:", countdownConfig.target_date);
        return;
      }

      // 创建倒计时显示容器
      const countdownDisplay = document.createElement("div");
      countdownDisplay.classList.add("zz_qab-countdown-display");
      countdownDisplay.style.display = "flex";
      countdownDisplay.style.alignItems = "center";
      countdownDisplay.style.justifyContent = "center"; // 默认居中，CSS会覆盖
      countdownDisplay.style.gap = "0px";

      // 创建倒计时更新函数
      const updateCountdown = () => {
        const now = new Date();
        const timeLeft = targetDate - now;

        if (timeLeft <= 0) {
          // 倒计时结束
          countdownDisplay.innerHTML = countdownConfig.end_text || "";
          return;
        }

        // 计算时间差
        const days = Math.max(0, Math.floor(timeLeft / (1000 * 60 * 60 * 24)));
        const hours = Math.max(0, Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
        const minutes = Math.max(0, Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60)));
        const seconds = Math.max(0, Math.floor((timeLeft % (1000 * 60)) / 1000));

        // 检查是否启用翻转动画并且有时间变化
        if (countdownConfig.flip_animation && countdownConfig.format === "boxed") {
          const currentTime = { days, hours, minutes, seconds };
          
          // 检查是否有时间变化，如果有则更新翻转动画
          if (this.flipState.previousTime.days !== null) {
            // 动态计算天数位数
            const daysDigits = days > 0 ? days.toString().length : 0;
            const prevDaysDigits = this.flipState.previousTime.days > 0 ? this.flipState.previousTime.days.toString().length : 0;
            
            const daysStr = days > 0 ? days.toString().padStart(Math.max(daysDigits, 2), '0') : '';
            const hoursStr = hours.toString().padStart(2, '0');
            const minutesStr = minutes.toString().padStart(2, '0');
            const secondsStr = seconds.toString().padStart(2, '0');
            
            const prevDaysStr = this.flipState.previousTime.days > 0 ? this.flipState.previousTime.days.toString().padStart(Math.max(prevDaysDigits, 2), '0') : '';
            const prevHoursStr = this.flipState.previousTime.hours.toString().padStart(2, '0');
            const prevMinutesStr = this.flipState.previousTime.minutes.toString().padStart(2, '0');
            const prevSecondsStr = this.flipState.previousTime.seconds.toString().padStart(2, '0');
            
            // 更新翻转动画 - 处理天数的动态位数
            if (days > 0) {
              for (let i = 0; i < daysStr.length; i++) {
                const currentDigit = daysStr[i];
                const prevDigit = i < prevDaysStr.length ? prevDaysStr[i] : '0';
                if (currentDigit !== prevDigit) {
                  this.updateFlipDigit(`day-${i}`, currentDigit);
                }
              }
            }
            
            // 更新其他时间单位（小时、分钟、秒）
            if (hoursStr[0] !== prevHoursStr[0]) this.updateFlipDigit('hour-0', hoursStr[0]);
            if (hoursStr[1] !== prevHoursStr[1]) this.updateFlipDigit('hour-1', hoursStr[1]);
            if (minutesStr[0] !== prevMinutesStr[0]) this.updateFlipDigit('minute-0', minutesStr[0]);
            if (minutesStr[1] !== prevMinutesStr[1]) this.updateFlipDigit('minute-1', minutesStr[1]);
            if (secondsStr[0] !== prevSecondsStr[0]) this.updateFlipDigit('second-0', secondsStr[0]);
            if (secondsStr[1] !== prevSecondsStr[1]) this.updateFlipDigit('second-1', secondsStr[1]);
          }
          
          // 更新上次时间记录
          this.flipState.previousTime = currentTime;
          
          // 如果已经有翻转数字容器，直接返回（翻转动画会自动更新）
          if (countdownDisplay.querySelector('.digit-flip-container')) {
            return;
          }
        }

        // 根据配置格式化显示
        if (countdownConfig.format === "boxed") {
          // 盒子格式：数字显示在独立背景框中
          countdownDisplay.innerHTML = "";
          
          // 添加前缀文本
          if (countdownConfig.prefix) {
            const prefixSpan = document.createElement("span");
            prefixSpan.classList.add("zz_qab-countdown-prefix");
            prefixSpan.textContent = countdownConfig.prefix;
            countdownDisplay.appendChild(prefixSpan);
          }
          
          // 创建时间数字容器
          const timeContainer = document.createElement("div");
          timeContainer.style.display = "flex";
          timeContainer.style.alignItems = "center";
          timeContainer.style.gap = "1px";
          
          // 预先计算背景样式
          const backgroundColor = countdownConfig.background_color || "#FF9B26";
          const useGradient = countdownConfig.use_gradient || false;
          const boxBackground = useGradient 
            ? this.generateCountdownGradient(backgroundColor)
            : backgroundColor;
          
          // 创建数字框样式函数
          const createTimeBox = (value, isColon = false, elementId = null) => {
            if (isColon) {
              // 冒号样式，使用内联样式标记以便CSS选择器识别
              const box = document.createElement("span");
              box.textContent = value;
              box.style.background = "none";
              box.style.padding = "0 1px";
              box.style.fontWeight = "bold";
              box.style.color = "inherit";
              box.style.fontSize = "inherit";
              box.style.fontFamily = "inherit";
              return box;
            }
            
            // 检查是否启用翻转动画
            if (countdownConfig.flip_animation && elementId) {
              const { container } = this.createFlipTimeBox(value, elementId, countdownConfig);
              return container;
            }
            
            // 普通数字盒子样式，根据配置设置背景
            const box = document.createElement("span");
            box.textContent = value;
            if (useGradient) {
              box.style.background = boxBackground;
            } else {
              box.style.backgroundColor = boxBackground;
            }
            box.style.color = "inherit";
            box.style.fontSize = "inherit";
            box.style.fontFamily = "inherit";
            return box;
          };
          
          if (days > 0) {
            // 显示天数格式，动态支持多位数天数：[D...D]:[H][H]:[M][M]:[S][S]
            const daysDigits = days.toString().length;
            const daysStr = days.toString().padStart(Math.max(daysDigits, 2), '0');
            
            // 动态创建天数的每个数字框
            for (let i = 0; i < daysStr.length; i++) {
              timeContainer.appendChild(createTimeBox(daysStr[i], false, `day-${i}`));
            }
            timeContainer.appendChild(createTimeBox(":", true));
            
            const hoursStr = hours.toString().padStart(2, '0');
            timeContainer.appendChild(createTimeBox(hoursStr[0], false, 'hour-0'));
            timeContainer.appendChild(createTimeBox(hoursStr[1], false, 'hour-1'));
            timeContainer.appendChild(createTimeBox(":", true));
            const minutesStr = minutes.toString().padStart(2, '0');
            timeContainer.appendChild(createTimeBox(minutesStr[0], false, 'minute-0'));
            timeContainer.appendChild(createTimeBox(minutesStr[1], false, 'minute-1'));
            timeContainer.appendChild(createTimeBox(":", true));
            const secondsStr = seconds.toString().padStart(2, '0');
            timeContainer.appendChild(createTimeBox(secondsStr[0], false, 'second-0'));
            timeContainer.appendChild(createTimeBox(secondsStr[1], false, 'second-1'));
          } else {
            // 显示时分秒格式，每个数字字符为独立盒子：[H][H]:[M][M]:[S][S]
            const hoursStr = hours.toString().padStart(2, '0');
            timeContainer.appendChild(createTimeBox(hoursStr[0], false, 'hour-0'));
            timeContainer.appendChild(createTimeBox(hoursStr[1], false, 'hour-1'));
            timeContainer.appendChild(createTimeBox(":", true));
            const minutesStr = minutes.toString().padStart(2, '0');
            timeContainer.appendChild(createTimeBox(minutesStr[0], false, 'minute-0'));
            timeContainer.appendChild(createTimeBox(minutesStr[1], false, 'minute-1'));
            timeContainer.appendChild(createTimeBox(":", true));
            const secondsStr = seconds.toString().padStart(2, '0');
            timeContainer.appendChild(createTimeBox(secondsStr[0], false, 'second-0'));
            timeContainer.appendChild(createTimeBox(secondsStr[1], false, 'second-1'));
          }
          
          countdownDisplay.appendChild(timeContainer);
          
          // 添加后缀文本
          if (countdownConfig.suffix) {
            const suffixSpan = document.createElement("span");
            suffixSpan.textContent = countdownConfig.suffix;
            suffixSpan.style.marginLeft = "8px";
            suffixSpan.style.color = "inherit";
            suffixSpan.style.width = "auto"; // 标识：排除数字盒子样式
            countdownDisplay.appendChild(suffixSpan);
          }
                  } else {
            // 数字格式：支持动态天数位数，如 365:12:05:30 或 05:30:45（纯数字格式）
          let displayText = "";
          if (days > 0) {
            // 动态计算天数位数，至少保证2位
            const daysDigits = days.toString().length;
            const daysStr = days.toString().padStart(Math.max(daysDigits, 2), '0');
            displayText = `${daysStr}:${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          } else {
            displayText = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          }
          
          // 添加前缀和后缀
          const prefix = countdownConfig.prefix || "";
          const suffix = countdownConfig.suffix || "";
          countdownDisplay.textContent = `${prefix}${displayText}${suffix}`;
        }
      };

      // 初始更新
      updateCountdown();

      // 设置定时更新（每秒）
      const intervalId = setInterval(updateCountdown, 1000);

      // 存储定时器ID，用于清理
      if (!this._countdownIntervals) {
        this._countdownIntervals = [];
      }
      this._countdownIntervals.push(intervalId);

      container.appendChild(countdownDisplay);
    }

    /**
     * 截断文本到指定长度
     * @param {string} text - 原文本
     * @param {number} maxLength - 最大长度
     * @returns {string} 截断后的文本
     * @private
     */
    truncateText(text, maxLength) {
      if (!text || text.length <= maxLength) {
        return text;
      }
      return text.substring(0, maxLength) + "...";
    }
    
    /**
     * 从HTML字符串中移除HTML标签
     * @param {string} html - 包含HTML标签的字符串
     * @returns {string} 移除HTML标签后的纯文本
     * @private
     */
    stripHtml(html) {
      if (!html || typeof html !== 'string') {
        return '';
      }
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    }
    
    /**
     * 净化HTML内容，移除潜在有害标签
     * @param {string} html - 原始HTML内容
     * @returns {string} 净化后的HTML内容
     * @private
     */
    sanitizeHtml(html) {
      if (!html || typeof html !== 'string') {
        return '';
      }
      
      // 简单的HTML净化
      // 移除script和iframe标签
      return html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');
    }
  
    /**
     * 绑定事件
     * @private
     */
    bindEvents() {
      // 鼠标悬停暂停轮播
      this.state.container.addEventListener("mouseenter", this.pause.bind(this));
      this.state.container.addEventListener("mouseleave", this.resume.bind(this));
  
      // 响应窗口大小变化
      window.addEventListener("resize", this.handleResize.bind(this));
      
      // 设置动态样式（如Home链接的显示条件）
      this.setupDynamicStyles();
    }
  
    /**
     * 设置 Home 链接的动态显示条件
     * @private
     */
    setupDynamicStyles() {
      // 获取配置的最小宽度
      const minWidth = this.config.home_link_min_width || 1400;
      
      // 更新或创建样式表
      let styleSheet = document.getElementById('zz-qab-dynamic-styles');
      if (!styleSheet) {
        styleSheet = document.createElement('style');
        styleSheet.id = 'zz-qab-dynamic-styles';
        document.head.appendChild(styleSheet);
      }
      
      // 只处理需要动态配置的样式
      styleSheet.textContent = `
        @media (min-width: ${minWidth}px) {
          .zz_qab-home-link {
            display: block !important;
          }
        }
      `;
    }
  
    /**
     * 处理窗口大小变化
     * @private
     */
    handleResize() {
      // 响应式调整容器高度
      if (this.state.container) {
        this.applyResponsiveHeight(this.state.container);
      }

      // 响应式调整 spacer 高度
      if (this.state.spacer) {
        this.applyResponsiveHeightToSpacer(this.state.spacer);
      }
  
      // 这里可以添加其他响应式调整逻辑
      // 例如，在移动设备上调整字体大小等
    }
  
    /**
     * 播放轮播
     * @public
     */
    play() {
      // 如果已经在播放，则不重复启动
      if (this.state.isPlaying && this.state.intervalId) {
        return;
      }
      
      // 如果只有一个项目或没有项目，不启动轮播
      if (this.config.items.length <= 1) {
        return;
      }
  
      // 设置定时器
      this.state.intervalId = setInterval(() => {
        this.next();
      }, this.config.change_slides_speed * 1000);
  
      this.state.isPlaying = true;
    }
  
    /**
     * 暂停轮播
     * @public
     */
    pause() {
      if (this.state.intervalId) {
        clearInterval(this.state.intervalId);
        this.state.intervalId = null;
      }
  
      this.state.isPlaying = false;
    }
  
    /**
     * 恢复轮播
     * @public
     */
    resume() {
      // 如果只有一个项目，不恢复轮播
      if (this.config.items.length <= 1) {
        return;
      }
      
      if (!this.state.isPlaying) {
        this.play();
      }
    }
  
    /**
     * 切换到下一个项目
     * @public
     */
    next() {
      // 如果动画正在进行中，不执行新的切换
      if (this.state.isAnimating) {
        return;
      }
      
      const prevIndex = this.state.currentIndex;
      const nextIndex = (prevIndex + 1) % this.state.itemElements.length;

      this.showItem(nextIndex, "next");
    }
  
    /**
     * 切换到上一个项目
     * @public
     */
    prev() {
      // 如果动画正在进行中，不执行新的切换
      if (this.state.isAnimating) {
        return;
      }
      
      const prevIndex = this.state.currentIndex;
      const nextIndex =
        (prevIndex - 1 + this.state.itemElements.length) %
        this.state.itemElements.length;

      this.showItem(nextIndex, "prev");
    }
  
    /**
     * 显示指定项目
     * @param {number} index - 项目索引
     * @param {string} direction - 切换方向 ('next' 或 'prev')
     * @public
     */
    showItem(index, direction = "next") {
      // 如果只有一个项目，不执行动画
      if (this.state.itemElements.length <= 1) {
        return;
      }
      
      // 如果动画正在进行中，不执行新的切换
      if (this.state.isAnimating) {
        return;
      }
      
      // 设置动画锁
      this.state.isAnimating = true;
      
      const currentIndex = this.state.currentIndex;
      const nextIndex = index;
  
      // 检查是否是从最后一个项目切换到第一个项目（循环）
      const isLooping =
        currentIndex === this.state.itemElements.length - 1 && nextIndex === 0;
  
      // 获取当前和下一个元素
      const currentItem = this.state.itemElements[currentIndex];
      const nextItem = this.state.itemElements[nextIndex];
  
      // 获取背景层
      const currentBg = this.state.backgroundElements[currentIndex];
      const nextBg = this.state.backgroundElements[nextIndex];
  
      // 获取内容层
      const currentContent = this.state.contentElements[currentIndex];
      const nextContent = this.state.contentElements[nextIndex];
  
      // 检测是否为移动设备
      const isMobileView = window.matchMedia("(max-width: 768px)").matches;
  
      // 首先显示下一个项目元素（背景层）
      nextItem.style.opacity = "1";
      nextItem.style.zIndex = "1";
  
      // 确保下一个内容层在开始前是可见的
      nextContent.style.display = "flex";
  
      // 准备设置动画前的初始状态
      const prepareAnimation = () => {
        // 重置动画状态
        if (this.config.animationType === "slide") {
          const isHorizontal = this.config.slideDirection === "horizontal";
          const slideValue = direction === "next" ? 100 : -100;
          const mobileSlideValue = direction === "next" ? 50 : -50;
  
          // 根据设备类型和方向设置初始位置
          if (isHorizontal) {
            const value = isMobileView ? mobileSlideValue : slideValue;
            nextContent.style.transform = `translateX(${-value}%)`;
          } else {
            const value = isMobileView ? mobileSlideValue : slideValue;
            nextContent.style.transform = `translateY(${-value}%)`;
          }
        } else if (this.config.animationType === "zoom") {
          const scaleValue = isMobileView ? 0.9 : 0.8;
          nextContent.style.transform = `scale(${scaleValue})`;
          nextContent.style.opacity = "0";
        } else {
          // fade动画只需要透明度
          nextContent.style.opacity = "0";
        }
  
        // 强制浏览器重新计算样式
        void nextContent.offsetWidth;
      };
  
      // 执行动画
      const executeAnimation = () => {
        // 设置不同设备类型的动画
        if (this.config.animationType === "slide") {
          const isHorizontal = this.config.slideDirection === "horizontal";
          const slideValue = direction === "next" ? 100 : -100;
          const mobileSlideValue = direction === "next" ? 50 : -50;
  
          if (isHorizontal) {
            const value = isMobileView ? mobileSlideValue : slideValue;
            currentContent.style.transform = `translateX(${value}%)`;
            nextContent.style.transform = "translateX(0)";
          } else {
            const value = isMobileView ? mobileSlideValue : slideValue;
            currentContent.style.transform = `translateY(${value}%)`;
            nextContent.style.transform = "translateY(0)";
          }
        } else if (this.config.animationType === "zoom") {
          const currentScaleValue = isMobileView ? 1.1 : 1.2;
          currentContent.style.transform = `scale(${currentScaleValue})`;
          currentContent.style.opacity = "0";
          nextContent.style.transform = "scale(1)";
          nextContent.style.opacity = "1";
        } else {
          // fade动画
          currentContent.style.opacity = "0";
          nextContent.style.opacity = "1";
        }
      };
  
      // 准备动画初始状态
      prepareAnimation();
  
      // 使用requestAnimationFrame确保动画顺利执行
      requestAnimationFrame(() => {
        // 确保下一个内容是可见的
        nextContent.style.opacity = "1";
  
        // 执行动画
        executeAnimation();
  
        // 在动画完成后，隐藏当前项目
        setTimeout(() => {
          currentItem.style.opacity = "0";
          currentItem.style.zIndex = "0";
          // 释放动画锁
          this.state.isAnimating = false;
        }, this.config.animationDuration);
      });
  
      // 更新当前索引
      this.state.currentIndex = nextIndex;
    }
  
    /**
     * 更新配置
     * @param {Object} newConfig - 新配置
     * @public
     */
    update(newConfig) {
      // 暂停轮播
      this.pause();
      
      // 重置动画状态锁
      this.state.isAnimating = false;

      // 清理旧的倒计时定时器
      if (this._countdownIntervals) {
        this._countdownIntervals.forEach(intervalId => {
          clearInterval(intervalId);
        });
        this._countdownIntervals = [];
      }

      // 清理 link_map 元素集合
      if (this._linkMapElements) {
        this._linkMapElements = [];
      }

      // 更新配置
      this.config = { ...this.config, ...newConfig };
  
      // 重新验证和处理配置
      this.validateAndProcessConfig();

      // 更新容器高度（如果高度配置改变了）
      if (this.state.container) {
        this.applyResponsiveHeight(this.state.container);
      }

      // 更新 spacer 高度
      if (this.state.spacer) {
        this.applyResponsiveHeightToSpacer(this.state.spacer);
      }
  
      // 重新创建项目
      this.createItems();
  
      // 只有当有多个项目时才开始轮播
      if (this.config.items.length > 1) {
        this.play();
      }
    }
  
    /**
     * 销毁组件
     * @public
     */
    destroy() {
      // 停止轮播
      this.pause();
      
      // 重置动画状态锁
      this.state.isAnimating = false;

      // 清理倒计时定时器
      if (this._countdownIntervals) {
        this._countdownIntervals.forEach(intervalId => {
          clearInterval(intervalId);
        });
        this._countdownIntervals = [];
      }

      // 清理 link_map 元素集合
      if (this._linkMapElements) {
        this._linkMapElements = [];
      }

      // 清理翻转动画状态
      this.flipState = {
        previousTime: { days: null, hours: null, minutes: null, seconds: null },
        animationInProgress: new Set(),
        digitElements: new Map()
      };

      // 移除事件监听
      this.state.container.removeEventListener("mouseenter", this.pause);
      this.state.container.removeEventListener("mouseleave", this.resume);
      window.removeEventListener("resize", this.handleResize);

      // 清理 spacer 元素
      if (this.state.spacer) {
        this.state.spacer.style.display = 'none';
        // 可选：完全移除 spacer 元素
        // this.state.spacer.remove();
        this.state.spacer = null;
      }
  
      // 清空容器
      this.state.container.innerHTML = "";
    }
  
    /**
     * 应用响应式高度设置
     * @param {HTMLElement} container - 容器元素
     * @private
     */
    applyResponsiveHeight(container) {
      // 获取配置的高度值
      const heightConfig = this.config.height;
      const mobileBreakpoint = this.config.mobileBreakpoint || 768;
  
      // 检测当前设备类型
      const isMobileView = window.matchMedia(
        `(max-width: ${mobileBreakpoint}px)`
      ).matches;
  
      // 确保height配置格式正确
      let height;
      if (typeof heightConfig === "object") {
        // 根据设备类型选择高度
        height = isMobileView
          ? heightConfig.mobile || heightConfig.desktop || "40px"
          : heightConfig.desktop || "50px";
      } else {
        // 兼容旧的字符串格式配置
        height = heightConfig || "50px";
      }
  
      // 应用高度
      container.style.height = height;
    }
  
    /**
     * 挂载到DOM
     * @param {string} selector - 容器选择器
     * @public
     */
    mount(selector) {
      if (selector) {
        this.config.containerSelector = selector;
  
        // 重新初始化
        this.initialize();
      }
    }
  
    /**
     * 节流函数，用于优化事件处理
     * @param {Function} func - 要节流的函数
     * @param {number} delay - 延迟时间（毫秒）
     * @returns {Function} 节流后的函数
     * @private
     */
    throttle(func, delay) {
      let lastCall = 0;
      return function (...args) {
        const now = new Date().getTime();
        if (now - lastCall < delay) {
          return;
        }
        lastCall = now;
        return func(...args);
      };
    }
  }
  